import { motion, AnimatePresence } from "framer-motion";
import { Check, X, ExternalLink, Settings, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

// Platform configuration for beautiful modals
const PLATFORM_CONFIG = {
  facebook: {
    name: "Facebook",
    color: "#1877F2",
    gradient: "from-[#1877F2] to-[#0a52a8]",
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    permissions: ["Manage Pages", "Publish Content", "View Insights"]
  },
  instagram: {
    name: "Instagram",
    color: "#E4405F",
    gradient: "from-[#f09433] via-[#e6683c] to-[#bc1888]",
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    permissions: ["Post Content", "View Insights", "Manage Comments"]
  },
  tiktok: {
    name: "TikTok",
    color: "#000000",
    gradient: "from-[#ff0050] via-[#00f2ea] to-[#ff0050]",
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
    permissions: ["Post Videos", "View Profile", "Manage Settings"],
    note: "TikTok requires per-post consent for privacy settings"
  },
  pinterest: {
    name: "Pinterest",
    color: "#E60023",
    gradient: "from-[#E60023] to-[#bd001c]",
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
      </svg>
    ),
    permissions: ["Create Pins", "View Boards", "Manage Content"]
  }
};

export function ConnectionSuccessModal({ 
  isOpen, 
  onClose, 
  platform, 
  accountName = "Your Account",
  accountHandle = "",
  onStartCreating 
}) {
  if (!isOpen || !platform) return null;
  
  const config = PLATFORM_CONFIG[platform.toLowerCase()] || PLATFORM_CONFIG.facebook;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-gradient-to-br from-viraly-dark to-[#1a1a2e] rounded-2xl p-8 max-w-md w-full border border-white/10 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-viraly-muted hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Success Icon with Platform Color */}
          <div className="flex justify-center mb-6">
            <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center relative`}>
              <div className="text-white">{config.icon}</div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center border-2 border-viraly-dark">
                <Check className="w-5 h-5 text-white" strokeWidth={3} />
              </div>
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">{config.name} Connected!</h2>
            <p className="text-viraly-muted">
              Your {config.name} account has been successfully connected to Viraly.
            </p>
          </div>

          {/* Account Info Card */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center text-white`}>
                {config.icon}
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold">{accountName || `${config.name} Account`}</div>
                {accountHandle && <div className="text-viraly-muted text-sm">@{accountHandle}</div>}
              </div>
              <div className="flex items-center gap-1.5 text-green-400 text-sm font-medium">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                Connected
              </div>
            </div>
          </div>

          {/* Permissions Summary */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
            <h3 className="text-blue-400 text-sm font-semibold mb-3 flex items-center gap-2">
              <Check className="w-4 h-4" />
              Authorized Permissions
            </h3>
            <div className="flex flex-wrap gap-2">
              {config.permissions.map((permission, idx) => (
                <span 
                  key={idx}
                  className="text-xs bg-white/10 text-white/90 px-3 py-1.5 rounded-full flex items-center gap-1.5"
                >
                  <Check className="w-3 h-3 text-green-400" />
                  {permission}
                </span>
              ))}
            </div>
            {config.note && (
              <p className="text-xs text-blue-300/70 mt-3">{config.note}</p>
            )}
          </div>

          {/* What's Next Section */}
          <div className="bg-viraly-orange/10 border border-viraly-orange/30 rounded-xl p-4 mb-6">
            <h3 className="text-viraly-orange text-sm font-semibold mb-2">What's Next?</h3>
            <ul className="text-white/80 text-sm space-y-1.5">
              <li className="flex items-center gap-2">
                <Plus className="w-3 h-3 text-viraly-orange" />
                Create your first {config.name} post
              </li>
              <li className="flex items-center gap-2">
                <Settings className="w-3 h-3 text-viraly-orange" />
                Set up Auto-Pilot for hands-free posting
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={onStartCreating}
              className="flex-1 bg-gradient-to-r from-viraly-orange to-amber-500 hover:from-viraly-orange/90 hover:to-amber-500/90 text-white font-semibold"
            >
              <Plus className="w-5 h-5 mr-2" />
              Start Creating
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="bg-white/5 border-white/20 text-white hover:bg-white/10"
            >
              Done
            </Button>
          </div>

          {/* Footer Note */}
          <p className="text-center text-viraly-muted text-xs mt-4">
            Manage connected accounts in{" "}
            <button onClick={onClose} className="text-viraly-orange hover:underline">
              Settings → Social
            </button>
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function DisconnectConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  platform,
  accountName
}) {
  if (!isOpen || !platform) return null;
  
  const config = PLATFORM_CONFIG[platform.toLowerCase()] || PLATFORM_CONFIG.facebook;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-gradient-to-br from-viraly-dark to-[#1a1a2e] rounded-2xl p-8 max-w-md w-full border border-white/10 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-viraly-muted hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex justify-center mb-6">
            <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center opacity-50`}>
              <div className="text-white">{config.icon}</div>
            </div>
          </div>

          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-white mb-2">Disconnect {config.name}?</h2>
            <p className="text-viraly-muted">
              This will remove {accountName || `your ${config.name} account`} from Viraly. You won't be able to post to this account until you reconnect.
            </p>
          </div>

          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
            <h3 className="text-red-400 text-sm font-semibold mb-2">What happens:</h3>
            <ul className="text-white/70 text-sm space-y-1">
              <li>• Scheduled posts to this account will be paused</li>
              <li>• Auto-Pilot will stop posting to {config.name}</li>
              <li>• Your existing posts remain on {config.name}</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 bg-white/5 border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
            >
              Disconnect
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ConnectionSuccessModal;
